/**
 * useChartMindI2Access — gate for ChartMind-I2 product surfaces.
 *
 * True when:
 * - JWT global admin, OR
 * - chartMindI2Access/{uid} exists
 *
 * Mirrors useClinicalHudAccess (admin bypass = isGlobalAdmin only).
 */
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "hooks/useAuth";
import { subscribeToChartMindI2Access } from "../services/chartMindI2Access";

export function useChartMindI2Access() {
  const { user, isGlobalAdmin, userLoading } = useAuth();
  const [hasDocAccess, setHasDocAccess] = useState(false);
  const [docLoading, setDocLoading] = useState(true);
  const [accessError, setAccessError] = useState(null);
  const [retryKey, setRetryKey] = useState(0);

  const uid = user?.uid ?? null;
  const isAnonymous = Boolean(user?.isAnonymous);
  const isRealAccount = Boolean(uid) && !isAnonymous;
  const privileged = Boolean(isGlobalAdmin);

  useEffect(() => {
    if (userLoading) {
      setDocLoading(true);
      return undefined;
    }

    if (!isRealAccount || privileged) {
      setHasDocAccess(false);
      setAccessError(null);
      setDocLoading(false);
      return undefined;
    }

    setDocLoading(true);
    setAccessError(null);
    const unsub = subscribeToChartMindI2Access(
      uid,
      (data) => {
        setHasDocAccess(Boolean(data));
        setAccessError(null);
        setDocLoading(false);
      },
      (err) => {
        setHasDocAccess(false);
        setAccessError(err || new Error("unknown"));
        setDocLoading(false);
      },
    );
    return () => unsub();
  }, [uid, isRealAccount, userLoading, privileged, retryKey]);

  const canAccess = Boolean(isRealAccount && (privileged || hasDocAccess) && !accessError);
  const loading = Boolean(
    userLoading || (isRealAccount && !privileged && docLoading),
  );
  const accessState = loading
    ? "loading"
    : accessError
      ? "error"
      : canAccess
        ? "granted"
        : "denied";

  const retry = useCallback(() => {
    setAccessError(null);
    setDocLoading(true);
    setRetryKey((key) => key + 1);
  }, []);

  return {
    canAccess: loading ? false : canAccess,
    loading,
    accessState: loading ? "loading" : accessState,
    errorCode: accessError ? String(accessError.code || accessError.message || "unknown") : "",
    retry,
  };
}

export default useChartMindI2Access;
